import React, { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Terminal, Users, DollarSign, Calendar, Check, ArrowRight, ChevronLeft,
  Globe, Sparkles, Loader2, ChevronDown, ChevronUp, Trophy, Crown,
} from "lucide-react";
import usePageview from "@/lib/usePageview";

// Phase 25.11 — Public Experts / Analyst recruitment landing.
// Sells the referral programme to data analysts worldwide. Live-sessions
// product is teased as "coming soon" — actual booking ships in Phase 2.

const BACKEND = (process.env.REACT_APP_BACKEND_URL || window.location.origin).replace(/\/$/, "");

const PERKS = [
  {
    icon: DollarSign,
    headline: "20% MRR for 12 months",
    sub: "Every customer you refer earns you a recurring tier-based commission for the first 12 months of their subscription. Senior-tier analysts and high performers earn at a higher rate — talk to us for current commission structure.",
  },
  {
    icon: Calendar,
    headline: "Get paid for sessions (Phase 2)",
    sub: "When a DARA customer needs a human expert, we route them to you. $99–$349 per session, 60% to you. Coming next quarter.",
  },
  {
    icon: Globe,
    headline: "Work from anywhere",
    sub: "AU, NZ, India, Nigeria, South Africa, UK, US — wherever you're based, your local hours fit a DARA customer somewhere.",
  },
  {
    icon: Sparkles,
    headline: "Senior tier = senior pay",
    sub: "Standard analyst tier is the entry point. Prove yourself (≥5 referrals, ≥4.5 NPS over 6 months) and graduate to Senior — higher session rates, priority routing.",
  },
];

const FAQ = [
  {
    q: "Who qualifies as a DARA Expert?",
    a: "Data analysts, data scientists, business analysts, and financial analysts with 2+ years of professional experience. We vet every application personally — LinkedIn, written bio, and (for senior tier) a short video answer to a scenario question.",
  },
  {
    q: "How does the referral programme actually work?",
    a: "Once approved, you get a unique referral code (e.g., AISHA-9K3X). Share daraos.ai/signup?ref=AISHA-9K3X with prospects. When they pay, you earn 20% of their MRR for 12 months. Payouts monthly via Stripe Connect once Stripe live keys are active (currently in test mode — earnings accrue and pay out when we flip).",
  },
  {
    q: "What's the catch on the 20% / 12 months?",
    a: "No catch. After 12 months of a customer's life, the commission ends — they remain DARA's customer at full price, and you've earned a tier-based recurring revenue share per referral over those 12 months. The exact rate is shared during analyst onboarding. We pick 12 months because it aligns your incentive to refer customers who'll actually stick, not just sign up.",
  },
  {
    q: "What's the difference between Standard and Senior tier?",
    a: "Standard: open to all qualified applicants. Session rate $99 (30min) / $199 (60min). Senior: ≥5 successful referrals AND ≥4.5 NPS over 6 months. Session rate $199 (30min) / $349 (60min). Priority routing for incoming session requests.",
  },
  {
    q: "When do live sessions go live?",
    a: "Phase 2 of this programme — building now, ships ~Q2 2026. The referral programme (Phase 1) is live today and earning. Sessions are stage 2.",
  },
  {
    q: "Where will I be paid?",
    a: "Stripe Connect Express handles payouts to 30+ countries in your local currency. You connect your bank once during onboarding, payouts arrive monthly automatically. Below $50 USD accrued, we roll over to next month.",
  },
  {
    q: "Can I work with multiple platforms?",
    a: "Yes — you're a contractor, not an employee. The only restriction is that during your engagement with a DARA customer, you can't poach them off-platform for 12 months after your last interaction. Standard marketplace clause.",
  },
];

export default function Experts() {
  usePageview("/experts");
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const [openFaq, setOpenFaq] = useState(null);
  const [leaders, setLeaders] = useState(null);

  useEffect(() => {
    fetch(`${BACKEND}/api/analysts/leaderboard`)
      .then((r) => r.json())
      .then((d) => setLeaders(d.leaderboard || []))
      .catch(() => setLeaders([]));
  }, []);

  return (
    <div className="min-h-screen bg-black text-white" data-testid="experts-page">
      {/* Top nav */}
      <header className="border-b border-zinc-900">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Terminal className="w-5 h-5 text-[#FFB000]" />
            <span className="font-heading font-black tracking-tight text-lg">DARA</span>
          </Link>
          <Link
            to="/experts/apply"
            data-testid="experts-nav-apply"
            className="bg-[#FFB000] text-black font-bold font-mono text-[11px] uppercase tracking-widest px-4 py-2 hover:opacity-90"
          >
            APPLY NOW →
          </Link>
        </div>
      </header>

      {/* HERO */}
      <section className="border-b border-zinc-900 py-14 sm:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FFB000] mb-4">
            // EXPERT PROGRAMME · DATA ANALYSTS & SCIENTISTS · WORLDWIDE
          </div>
          <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
            Earn from DARA<br />
            <span className="text-[#FFB000]">while businesses earn from you.</span>
          </h1>
          <p className="text-base sm:text-lg text-zinc-400 max-w-2xl mt-6 leading-relaxed">
            DARA is a Bloomberg-style analytics terminal for SMBs and mid-market operators
            globally. We need data analysts to refer customers, coach them through the data,
            and (coming soon) deliver paid 30–60 minute sessions when customers need a human.
            Get paid for both.
          </p>
          <div className="flex flex-wrap gap-3 mt-8">
            <Link
              to="/experts/apply"
              data-testid="experts-hero-apply"
              className="bg-[#FFB000] text-black font-bold font-mono text-[11px] uppercase tracking-widest px-5 py-3 hover:opacity-90 inline-flex items-center gap-2"
            >
              APPLY TO JOIN
              <ArrowRight className="w-3.5 h-3.5" />
            </Link>
            <a
              href="#how-it-works"
              className="border border-zinc-800 hover:border-zinc-700 px-5 py-3 font-mono text-[11px] uppercase tracking-widest text-zinc-300 inline-flex items-center gap-2"
            >
              HOW IT WORKS
            </a>
          </div>
        </div>
      </section>

      {/* PERKS */}
      <section id="how-it-works" className="py-14 border-b border-zinc-900">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FFB000] mb-3">
            // WHAT YOU EARN
          </div>
          <h2 className="font-heading font-black text-3xl sm:text-4xl tracking-tight mb-10">
            Two income streams. One vetting process.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {PERKS.map((p, i) => (
              <div
                key={i}
                data-testid={`experts-perk-${i}`}
                className="border border-zinc-900 bg-zinc-950/40 p-5 flex items-start gap-3"
              >
                <span className="border border-[#FFB000]/40 bg-[#FFB000]/10 p-2.5 flex-shrink-0">
                  <p.icon className="w-4 h-4 text-[#FFB000]" />
                </span>
                <div className="flex-1 min-w-0">
                  <div className="font-heading font-bold text-base">{p.headline}</div>
                  <div className="text-sm text-zinc-400 mt-1 leading-relaxed">{p.sub}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MATH */}
      <section className="py-14 border-b border-zinc-900 bg-zinc-950/30">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FFB000] mb-3">
            // DO THE MATH
          </div>
          <h2 className="font-heading font-black text-3xl sm:text-4xl tracking-tight mb-8">
            What 10 referrals look like.
          </h2>
          <div className="border border-zinc-900 bg-black p-5 sm:p-6 font-mono text-sm">
            <Row label="REFERRALS" value="10 customers" />
            <Row label="COMMISSION" value="Tier-based · revenue share per closed referral" />
            <Row label="STRUCTURE" value="Monthly recurring · paid for the customer lifetime · transparent statements" />
            <Row label="CURRENT RATE" value={<span className="text-[#FFB000] font-bold">Talk to us</span>} />
            <Row label="EFFORT" value="One LinkedIn post + 10 personal intros" last />
          </div>
          <p className="text-xs text-zinc-500 mt-4 italic leading-relaxed">
            Senior-tier analysts earn at a higher commission rate than Standard, and high-performers
            move from Standard to Senior after consistent referral track-record. The specific
            commission structure is shared during onboarding — we keep it conversation-led so we can
            evolve the programme as DARA grows without locking analysts into a published rate.
          </p>
          <div className="mt-6 grid sm:grid-cols-3 gap-px bg-zinc-900 border border-zinc-900" data-testid="experts-tier-payouts">
            {[
              { tier: "STARTER", featured: false },
              { tier: "SCALE", featured: true },
              { tier: "PROFESSIONAL", featured: false },
            ].map((t) => (
              <div key={t.tier} className={`bg-black p-4 ${t.featured ? "ring-1 ring-[#FFB000]/40" : ""}`}>
                <div className="font-mono text-[9px] uppercase tracking-widest text-[#FFB000]">{t.tier} {t.featured && "· MOST POPULAR"}</div>
                <div className="font-mono text-[10px] text-zinc-500 mt-1">Customer tier</div>
                <div className="font-mono text-[10px] text-zinc-400 mt-3">Commission · <span className="text-white">tier-based revenue share</span></div>
                <div className="font-mono text-[11px] text-[#FFB000] font-bold mt-1">Talk to us for current rates</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* LEADERBOARD */}
      <section className="py-14 border-b border-zinc-900" data-testid="experts-leaderboard-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-[#FFB000] mb-3">
            <Trophy className="w-3 h-3" /> // TOP EARNERS · LIVE
          </div>
          <h2 className="font-heading font-black text-3xl sm:text-4xl tracking-tight mb-3">
            Real analysts. Real earnings.
          </h2>
          <p className="text-sm text-zinc-400 leading-relaxed mb-6 max-w-2xl">
            Top 10 by lifetime earnings across the DARA programme. Updated nightly. Analysts can opt out of the public board if they prefer privacy.
          </p>

          {leaders === null && (
            <div className="font-mono text-xs text-zinc-500 flex items-center gap-2">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…
            </div>
          )}

          {leaders !== null && leaders.length === 0 && (
            <div
              data-testid="experts-leaderboard-empty"
              className="border border-dashed border-zinc-900 bg-zinc-950/30 p-6 text-center"
            >
              <Trophy className="w-6 h-6 text-zinc-700 mx-auto mb-3" />
              <div className="font-heading font-bold text-base">Be the first name here.</div>
              <p className="text-sm text-zinc-500 mt-2 leading-relaxed max-w-md mx-auto">
                Programme just launched. The first 10 approved analysts to earn anything appear on
                this public board — permanently. Apply now to claim spot #1.
              </p>
            </div>
          )}

          {leaders !== null && leaders.length > 0 && (
            <div
              data-testid="experts-leaderboard"
              className="border border-zinc-900 bg-zinc-950/40 overflow-hidden"
            >
              {leaders.map((row, i) => (
                <div
                  key={i}
                  data-testid={`leaderboard-row-${i + 1}`}
                  className={`flex items-center gap-3 px-4 sm:px-5 py-3 ${
                    i < leaders.length - 1 ? "border-b border-zinc-900" : ""
                  } ${i === 0 ? "bg-[#FFB000]/[0.05]" : ""}`}
                >
                  <div className={`w-8 text-center font-mono font-bold text-sm flex-shrink-0 ${
                    i === 0 ? "text-[#FFB000]" : i < 3 ? "text-white" : "text-zinc-600"
                  }`}>
                    #{i + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-heading font-bold text-sm sm:text-base text-white">
                        {row.first_name}
                      </span>
                      {row.tier === "senior" && (
                        <span className="px-1.5 py-0.5 border border-[#FFB000]/40 bg-[#FFB000]/10 text-[#FFB000] font-mono text-[9px] uppercase tracking-widest inline-flex items-center gap-1">
                          <Crown className="w-2.5 h-2.5" /> SENIOR
                        </span>
                      )}
                    </div>
                    <div className="font-mono text-[10px] text-zinc-500 mt-0.5">
                      {row.country} · {row.referrals_count} referral{row.referrals_count === 1 ? "" : "s"}
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className={`font-heading font-black text-base sm:text-lg tracking-tight ${
                      i === 0 ? "text-[#FFB000]" : "text-white"
                    }`}>
                      ${row.earnings_lifetime_usd.toLocaleString("en-US", { maximumFractionDigits: 0 })}
                    </div>
                    <div className="font-mono text-[9px] text-zinc-600 uppercase tracking-widest">
                      LIFETIME
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* FAQ */}
      <section className="py-14 border-b border-zinc-900">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FFB000] mb-3">
            // FAQ
          </div>
          <h2 className="font-heading font-black text-3xl sm:text-4xl tracking-tight mb-8">
            Straight answers.
          </h2>
          <div className="space-y-2">
            {FAQ.map((f, i) => (
              <div
                key={i}
                data-testid={`experts-faq-${i}`}
                className="border border-zinc-900 bg-zinc-950/40"
              >
                <button
                  type="button"
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between gap-4 text-left px-5 py-4 hover:bg-zinc-900/40"
                >
                  <span className="font-heading font-bold text-sm sm:text-base">{f.q}</span>
                  {openFaq === i ? (
                    <ChevronUp className="w-4 h-4 text-[#FFB000] flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-zinc-600 flex-shrink-0" />
                  )}
                </button>
                {openFaq === i && (
                  <div className="px-5 pb-4 text-sm text-zinc-400 leading-relaxed">{f.a}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="font-heading font-black text-3xl sm:text-4xl tracking-tight">
            Ready to apply?
          </h2>
          <p className="text-base text-zinc-400 mt-4 leading-relaxed">
            5 minutes. We review every application personally and respond within 3 business days.
          </p>
          <div className="mt-7">
            <Link
              to="/experts/apply"
              data-testid="experts-bottom-apply"
              className="bg-[#FFB000] text-black font-bold font-mono text-[11px] uppercase tracking-widest px-5 py-3 hover:opacity-90 inline-flex items-center gap-2"
            >
              <Users className="w-3.5 h-3.5" />
              APPLY TO JOIN
              <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
          <div className="mt-8">
            <Link
              to="/"
              className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 hover:text-white inline-flex items-center gap-1"
            >
              <ChevronLeft className="w-3 h-3" /> BACK TO HOMEPAGE
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

function Row({ label, value, last }) {
  return (
    <div className={`flex items-center justify-between gap-3 py-2 ${last ? "" : "border-b border-zinc-900"}`}>
      <span className="text-zinc-500 text-[10px] uppercase tracking-widest">{label}</span>
      <span className="text-white text-right">{value}</span>
    </div>
  );
}
